<template>
  <div>
    <Header />
    <v-container>
      <!-- SLA Regeln Tabelle -->
      <v-card class="mb-6">
        <v-card-title>
          SLA Versandregeln
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="openCreateDialog">
            <v-icon left>mdi-plus</v-icon>
            Neue Regel
          </v-btn>
        </v-card-title>
        <v-card-text>
          <p class="mb-4">
            Hier können SLA-Regeln für die automatische Berechnung des letzten Versanddatums (lastShipmentDate) verwaltet werden.
            Die Regeln berücksichtigen automatisch Wochenenden und deutsche Feiertage.
          </p>
          <v-data-table
            :headers="headers"
            :items="rules"
            :loading="loading"
            class="elevation-1"
            sort-by="priority"
            :sort-desc="true"
          >
            <template v-slot:item.cutoff="{ item }">
              {{ formatTime(item.cutoff) }}
            </template>

            <template v-slot:item.active="{ item }">
              <v-chip :color="item.active ? 'success' : 'error'" small>
                {{ item.active ? 'Aktiv' : 'Inaktiv' }}
              </v-chip>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn small icon color="primary" @click="editRule(item)">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn small icon color="red" @click="deleteRule(item)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- Test-Tool -->
      <v-card>
        <v-card-title>
          <v-icon left>mdi-calculator</v-icon>
          SLA Berechnung testen
        </v-card-title>
        <v-card-text>
          <p class="mb-4">
            Testen Sie die SLA-Berechnung mit einer echten Bestellnummer aus der Datenbank.
          </p>
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="testOrderId"
                label="Bestellnummer"
                placeholder="z.B. MM28369"
                outlined
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="testChannel"
                :items="['muckelmeister', 'amazon', 'etsy']"
                label="Channel"
                outlined
              ></v-select>
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="testShippingType"
                :items="['standard', 'express', 'prime']"
                label="Versandart"
                outlined
              ></v-select>
            </v-col>
            <v-col cols="12" md="2">
              <v-btn
                color="primary"
                large
                block
                @click="testCalculation"
                :loading="testLoading"
              >
                Berechnen
              </v-btn>
            </v-col>
          </v-row>

          <!-- Test-Ergebnis -->
          <v-alert v-if="testResult" type="success" class="mt-4">
            <div class="text-h6">Ergebnis für {{ testResult.orderId }}</div>
            <v-divider class="my-2"></v-divider>
            <v-row>
              <v-col cols="12" md="6">
                <div><strong>Bestelldatum:</strong> {{ formatDateTime(testResult.details.orderDate) }}</div>
                <div><strong>Nach Cutoff?</strong> {{ testResult.details.calculation.isAfterCutoff ? 'Ja' : 'Nein' }}</div>
                <div><strong>Angewandte Regel:</strong> {{ testResult.details.rule.description }}</div>
                <div><strong>Cutoff-Zeit:</strong> {{ testResult.details.rule.cutoff }}</div>
              </v-col>
              <v-col cols="12" md="6">
                <div><strong>SLA Tage:</strong> {{ testResult.details.rule.slaDays }}</div>
                <div><strong>Basis-Datum:</strong> {{ formatDate(testResult.details.calculation.baseDate) }}</div>
                <div><strong>Angepasstes Datum:</strong> {{ formatDate(testResult.details.calculation.adjustedBaseDate) }}</div>
                <div class="text-h6 mt-2 primary--text">
                  <strong>Last Shipment Date:</strong> {{ formatDate(testResult.lastShipmentDate) }}
                </div>
              </v-col>
            </v-row>
          </v-alert>

          <v-alert v-if="testError" type="error" class="mt-4">
            {{ testError }}
          </v-alert>
        </v-card-text>
      </v-card>

      <!-- Create/Edit Dialog -->
      <v-dialog v-model="dialog" max-width="600" persistent>
        <v-card>
          <v-card-title>
            {{ editedItem.id ? 'Regel bearbeiten' : 'Neue Regel erstellen' }}
          </v-card-title>
          <v-card-text>
            <v-form ref="form" v-model="valid">
              <v-text-field
                v-model="editedItem.channel"
                label="Channel *"
                :rules="[v => !!v || 'Erforderlich']"
                hint="z.B. muckelmeister, amazon, etsy"
                required
              ></v-text-field>

              <v-select
                v-model="editedItem.shippingType"
                :items="['standard', 'express', 'prime']"
                label="Versandart *"
                :rules="[v => !!v || 'Erforderlich']"
                required
              ></v-select>

              <v-text-field
                v-model.number="editedItem.slaDays"
                label="SLA Tage *"
                type="number"
                min="0"
                :rules="[v => v !== null && v >= 0 || 'Muss >= 0 sein']"
                hint="Anzahl Geschäftstage bis Versand"
                required
              ></v-text-field>

              <v-text-field
                v-model="editedItem.cutoff"
                label="Cutoff-Zeit (HH:MM) *"
                placeholder="13:00"
                :rules="[v => !!v || 'Erforderlich', v => /^\d{2}:\d{2}$/.test(v) || 'Format: HH:MM']"
                hint="Bestellungen nach dieser Zeit zählen als nächster Tag"
                required
              ></v-text-field>

              <v-textarea
                v-model="editedItem.description"
                label="Beschreibung"
                rows="2"
                hint="Beschreibung der Regel für bessere Übersicht"
              ></v-textarea>

              <v-text-field
                v-model.number="editedItem.priority"
                label="Priorität"
                type="number"
                min="0"
                hint="Höhere Priorität = wichtiger (bei mehreren passenden Regeln)"
              ></v-text-field>

              <v-checkbox
                v-model="editedItem.active"
                label="Aktiv"
              ></v-checkbox>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="closeDialog">Abbrechen</v-btn>
            <v-btn color="primary" :disabled="!valid" @click="saveRule">Speichern</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import Header from "./header.vue";
import Api from "@/services/Api";
import moment from "moment";

export default {
  name: "SlaRulesManager",
  components: { Header },
  data: () => ({
    rules: [],
    loading: false,
    dialog: false,
    valid: false,
    editedItem: {
      channel: 'muckelmeister',
      shippingType: 'standard',
      slaDays: 1,
      cutoff: '23:59',
      description: '',
      priority: 10,
      active: true
    },
    defaultItem: {
      channel: 'muckelmeister',
      shippingType: 'standard',
      slaDays: 1,
      cutoff: '23:59',
      description: '',
      priority: 10,
      active: true
    },
    headers: [
      { text: 'ID', value: 'id', sortable: true },
      { text: 'Channel', value: 'channel' },
      { text: 'Versandart', value: 'shippingType' },
      { text: 'SLA Tage', value: 'slaDays' },
      { text: 'Cutoff', value: 'cutoff' },
      { text: 'Beschreibung', value: 'description' },
      { text: 'Priorität', value: 'priority' },
      { text: 'Status', value: 'active' },
      { text: 'Aktionen', value: 'actions', sortable: false }
    ],
    // Test-Tool
    testOrderId: '',
    testChannel: 'muckelmeister',
    testShippingType: 'standard',
    testResult: null,
    testError: null,
    testLoading: false
  }),

  mounted() {
    this.loadRules();
  },

  methods: {
    async loadRules() {
      this.loading = true;
      try {
        const response = await Api().get('/sla/rules');
        this.rules = response.data;
      } catch (error) {
        console.error('Error loading SLA rules:', error);
        this.showError('Fehler beim Laden der SLA-Regeln');
      } finally {
        this.loading = false;
      }
    },

    openCreateDialog() {
      this.editedItem = Object.assign({}, this.defaultItem);
      this.dialog = true;
    },

    editRule(item) {
      this.editedItem = Object.assign({}, item);
      // Convert MySQL TIME to HH:MM format
      if (this.editedItem.cutoff) {
        this.editedItem.cutoff = this.editedItem.cutoff.substring(0, 5);
      }
      this.dialog = true;
    },

    closeDialog() {
      this.dialog = false;
      this.$nextTick(() => {
        this.$refs.form.reset();
      });
    },

    async saveRule() {
      if (!this.$refs.form.validate()) return;

      try {
        // Convert HH:MM to HH:MM:SS for MySQL
        const ruleData = {
          ...this.editedItem,
          cutoff: this.editedItem.cutoff + ':00'
        };

        if (this.editedItem.id) {
          await Api().put(`/sla/rules/${this.editedItem.id}`, ruleData);
        } else {
          await Api().post('/sla/rules', ruleData);
        }

        this.loadRules();
        this.closeDialog();
      } catch (error) {
        console.error('Error saving SLA rule:', error);
        this.showError('Fehler beim Speichern der Regel');
      }
    },

    async deleteRule(item) {
      if (!confirm(`Regel "${item.description}" wirklich löschen?`)) return;

      try {
        await Api().delete(`/sla/rules/${item.id}`);
        this.loadRules();
      } catch (error) {
        console.error('Error deleting SLA rule:', error);
        this.showError('Fehler beim Löschen der Regel');
      }
    },

    async testCalculation() {
      this.testLoading = true;
      this.testResult = null;
      this.testError = null;

      try {
        const response = await Api().post('/sla/test', {
          orderId: this.testOrderId,
          channel: this.testChannel,
          shippingType: this.testShippingType
        });

        this.testResult = response.data;
      } catch (error) {
        this.testError = error.response?.data?.message || 'Fehler bei der Berechnung';
        console.error('Test calculation error:', error);
      } finally {
        this.testLoading = false;
      }
    },

    formatTime(time) {
      if (!time) return '';
      return time.substring(0, 5); // HH:MM:SS -> HH:MM
    },

    formatDate(date) {
      return moment(date).format('DD.MM.YYYY');
    },

    formatDateTime(date) {
      return moment(date).format('DD.MM.YYYY HH:mm');
    },

    showError(message) {
      // You can implement a snackbar/toast notification here
      alert(message);
    }
  }
};
</script>

<style scoped>
.v-card {
  margin-bottom: 20px;
}
</style>
